import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { ResponsiveContainer, Legend, PieChart, Pie, Cell, Sector, Tooltip, Label } from 'recharts';
import { defaultNotifier } from 'functions/notificationHandler';
import Card from 'components/Detail/Card';
import { isEmpty, isEqual } from 'lodash';
import {
  GROUP_BY_UNIQUE_COLORS,
  getPieChartConfig,
  getActualTimeRange,
  chartGradientDefs,
  legendPayload,
  safeParseNumber,
  legendContent,
  roundData,
  formatYAxisLabel,
  getChartRenderRequirements,
  normalizePercentPieData,
  getThemeThenRandomSeriesColor,
  chartDotMenu
} from './utils'
import classNames from 'classnames';
import { ChartLoader } from './ChartLoader';
import ChartDotMenu from './ChartDotMenu';
import { rawChartData, previewRawChartData } from '../../../../../api/dashboard';
import { CHART_DEFAULT_VALUES } from '../chartConfigurationsV2'
import { BLOCK, WARN } from './constants'
import { THEME_COLOR_SWATCH } from 'utils/constants'

class DoughnutChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      chartData: {
        groupBy: [],
        data: []
      },
      metricColumns: [],
      validData: false,
      dataErrorMessage: '',
      dataWarnMessage: '',
      activeIndex: 0,
      uniqueGroupByValue: {},
      availableColorIndices: Array.from(Array(GROUP_BY_UNIQUE_COLORS.length).keys()),
      uniqueGroupByOriginalKey: {},
      isChartDataLoading: false,
      groupColors: [],
      chartConfig: { ...CHART_DEFAULT_VALUES },
      hasMenu: false,
      maxPreviewRawValue: null,
      actualDoughnutValue: null
    }
  }

  // eslint-disable-next-line
  componentWillReceiveProps(nextprops) {
    if (!isEqual(this.props.isDashboardInitialized, nextprops.isDashboardInitialized)) {
      if (nextprops.isPreview) {
        return this.setPreviewData()
      }
      if (nextprops.isDashboardInitialized) {
        return this.setData()
      }
    }

    if (nextprops.errors) {
      defaultNotifier(nextprops.errors, this.props);
    }
  }

  async componentDidMount() {
    const isOldChartConfig = typeof this.props.chart?.configuration === 'object' && this.props.chart?.configuration.hasOwnProperty('basicFields')
    this.setState({ hasMenu: !isOldChartConfig })

    if (!this.props.isPreview && !isOldChartConfig) {
      const chartConfig = JSON.parse(this.props.chart?.configuration)
      // For Doughnut charts at dashboard level, tiers/thresholds are not applicable. Sanitize loaded config.
      const sanitized = {
        ...chartConfig,
        colorTiers: { customConfigs: [] },
        enableThreshold: false,
        allThresholdType: 'absolute'
      }
      this.setState({ chartConfig: sanitized })//, () => { console.log('After parsing and saving - this.state.chartConfig: ', this.state.chartConfig)})
    }

    if (this.props.groupByColors) {
      this.setState({
        uniqueGroupByValue: { ...this.props.groupByColors }
      }, () => {
        const newChartData = this.state.chartData?.data?.map(metric => {
          return {
            ...metric,
            fill: this.getUniqueGroupByValue(metric.group)
          }
        })
        this.setState({
          chartData: newChartData
        })
      })
    }

    if (this.props.isPreview) {
      const sanitized = {
        ...this.props.chartValues,
        colorTiers: { customConfigs: [] },
        enableThreshold: false,
        allThresholdType: 'absolute'
      }
      this.setState({ chartConfig: sanitized, hasMenu: false }, () => {
        this.setPreviewData()
      })
    }
    // else {
    //   this.setData()
    // }
  }

  async componentDidUpdate(prevProps) {
    if (!isEqual(this.props.devicesData, prevProps.devicesData)) {
      return this.setPreviewData()
    }

    if (this.props.isPreview) {

      const currentChartValues = this.props.chartValues
      const prevChartValues = prevProps.chartValues

      if (!isEqual(this.props.chartType, prevProps.chartType)) {

        return this.setPreviewData()
      }

      if (!isEqual(currentChartValues, prevChartValues)) {
        this.setState({ chartConfig: { ...currentChartValues } }, () => {
          const currentDataStreams = this.props.dataStreams
          const prevDataStreams = prevProps.dataStreams

          if (!isEqual(currentDataStreams, prevDataStreams)) {
            return this.setPreviewData()
          }

          if (!isEqual(currentChartValues.devices, prevChartValues.devices)) {
            return this.setPreviewData()
          }

          if (!isEqual(currentChartValues.groupBy, prevChartValues.groupBy)) {
            return this.setPreviewData()
          }
        })
      }


      const currentDataStreams = this.props.dataStreams
      const prevDataStreams = prevProps.dataStreams

      if (!isEqual(currentDataStreams, prevDataStreams)) {
        return this.setPreviewData()
      }

      const currentChart = this.props.chart
      const prevChart = prevProps.chart
      const currentPreviewParams = this.props.previewParams
      const prevPreviewParams = prevProps.previewParams

      if (!isEqual(currentChart, prevChart) || !isEqual(currentPreviewParams, prevPreviewParams)) {
        return this.setPreviewData()
      }

      if (this.props.refreshPreview) {
        return this.setState({
          chartConfig: this.props.chartValues
        })
      }
    }

    if (!isEqual(this.props.devicesData, prevProps.devicesData)) {
      return this.setPreviewData()
    }

    const prevIsDashboardInitialized = prevProps.isDashboardInitialized
    const actualIsDashboardInitialized = this.props.isDashboardInitialized
    if ((prevIsDashboardInitialized !== actualIsDashboardInitialized) && actualIsDashboardInitialized) {
      this.setData()
    }
  }

  setPreviewData = async () => {
    const { chart, previewParams, dashboardTimeRange, isPreview } = this.props

    const actualTimeRange = getActualTimeRange(chart, dashboardTimeRange, isPreview)

    try {
      // const { hasBlockingIssues } = this.checkChartRenderRequirements(actualTimeRange)

      // if (hasBlockingIssues) {
      //   return this.setState({ isChartDataLoading: false })
      // }

      // Always use new version raw data flow
      this.setRawChartData()
    } catch (error) {
      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: error.message,
        validData: false
      })
    }
  }

  setPreviewData = async () => {
    const { chart, previewParams, dashboardTimeRange, isPreview } = this.props

    const isNewVersion = this.state.chartConfig.hasOwnProperty('version')

    const actualTimeRange = getActualTimeRange(chart, dashboardTimeRange, isPreview)

    try {
      // const { hasBlockingIssues } = this.checkChartRenderRequirements(actualTimeRange)

      // if (hasBlockingIssues) {
      //   return this.setState({ isChartDataLoading: false })
      // }

      if (isNewVersion) {
        this.setRawChartData()
      } else {
        const data = await fetchPreviewChartData(chart, previewParams, actualTimeRange)
        this.setChartData(data, chart)
      }
    } catch (error) {
      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: error.message,
        validData: false
      })
    }
  }

  setData = async () => {
    const { chart, dashboardTimeRange, dashboardDevices } = this.props
    this.setState({ isChartDataLoading: true })

    const actualTimeRange = getActualTimeRange(chart, dashboardTimeRange)

    try {
      const { hasBlockingIssues } = this.checkChartRenderRequirements(actualTimeRange)

      if (hasBlockingIssues) {
        return this.setState({ isChartDataLoading: false })
      }

      // Always use new version raw data flow
      this.setRawChartData()
    } catch (error) {
      this.setState({ isChartDataLoading: false })
      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: error.message,
        validData: false
      })
    }
  }

  checkChartRenderRequirements = (timeRange) => {
    const requirements = getChartRenderRequirements(this.state.chartConfig, timeRange)

    const warnMessages = requirements[WARN].map(r => r.message).join('\n')
    const blockMessages = requirements[BLOCK].map(r => r.message).join('\n')
    const hasWarningIssues = requirements[BLOCK].length > 0
    const hasBlockingIssues = requirements[BLOCK].length > 0

    if (hasWarningIssues) {
      this.setState({ dataWarnMessage: warnMessages })
    }

    if (hasBlockingIssues) {
      this.setState({
        validData: false,
        dataErrorMessage: blockMessages,
        chartData: { groupBy: [], data: [] }
      })
    }

    return {
      hasBlockingIssues
    }
  }

  setChartData = (data, chart) => {
    const { groupBy, metricsData, metricColumns } = data

    const chartConfig = getPieChartConfig(chart.configuration)

    const chartData = {
      groupBy: [...groupBy],
      data: [...metricsData]
    }

    if (isEmpty(chartData.groupBy)) {
      chartData.data = metricColumns.map(metric => {
        const res = {
          metric: metric,
          name: metric,
          value: 0,
          group: {}
        }
        if (isEmpty(metricsData)) {
          return res
        }
        const metricData = metricsData[0]
        res.value = safeParseNumber(metricData.values[metric]) || 0
        res.group = isEmpty(metricData.group) ? {} : { ...metricData.group }
        return res
      })
    }

    const validGroupBy = chartData.groupBy
      .filter(item => item !== 'time')[0] || ''

    if (validGroupBy) {
      chartData.data = chartData.data.map(metric => {
        const name = Object.values(metric.group)[0]
        const value = safeParseNumber(Object.values(metric.values)[0])
        return {
          ...metric,
          name: name,
          metric: name,
          value: value,
          fill: this.getUniqueGroupByValue(metric.group)
        }
      })
      chartData.data.sort((a, b) => a.name?.localeCompare(b, undefined, { sensitivity: 'base' }))
    } else {
      chartData.data = chartData.data.map((metric, index) => {
        return { ...metric }
      })
      this.setState({
        uniqueGroupByValue: {},
        availableColorIndices: Array.from(Array(GROUP_BY_UNIQUE_COLORS.length).keys())
      }, () => {
        if (this.props.onBarColorChange) {
          this.props.onBarColorChange(this.state.uniqueGroupByValue)
        }
      })
    }

    return this.setState({
      chartData: chartData,
      metricColumns: metricColumns,
      chartConfig: chartConfig,
      dataErrorMessage: '',
      validData: true
    })
  }

  onDoughnutEnter = (_, index) => {
    this.setState({
      activeIndex: index,
    })
  }

  onDoughnutLeave = () => {
    // Clear active slice after a short delay to avoid flicker across small gaps
    if (this._leaveTimer) {
      clearTimeout(this._leaveTimer)
    }
    this._leaveTimer = setTimeout(() => {
      this.setState({ activeIndex: -1 })
      this._leaveTimer = null
    }, 120)
  }

  getOverrideData = (data) => {
    const { chartData, chartConfig } = this.state

    if (chartData.data.length === 0) {
      return ''
    }

    if (chartConfig?.colorTiers?.customConfigs?.length > 0 && chartConfig?.enableThreshold) {

      const tiers = chartConfig.colorTiers.customConfigs;
      let labelText = tiers[0].text;
      let configData = tiers[0];

      for (const tier of tiers) {
        if (data > parseFloat(tier.value)) {
          labelText = tier.text;
          configData = tier;
        }
      }
      return configData

    }

    // const dataKey = Object.keys(chartData.data[0].values)[0]
    // const firstItem = chartData.data[0]
    // const values = firstItem?.values
    // const dataKey = values
    //   ? Object.keys(values)[0]
    //   : Object.keys(firstItem)[0]

    // const overrideValue = cardConfig?.dataOverrides[dataKey]?.value

    // if (overrideValue) {
    //   return overrideValue
    // }
    return null

  }

  getStrokeLine = (entry, index) => {
    const { chartConfig } = this.state

    const isCompare = chartConfig?.dataStreamSingle?.breakdownType === 'compare_streams'

    if (isCompare) {
      // Build palette from stream labels if available; fallback to alternating palette order
      const streams = chartConfig?.dataStreams?.streams || chartConfig?.dataStreamSingle?.dataStreams?.streams || []
      const used = []
      const streamPalette = streams.map((s, i) => {
        const key = s?.streamLabel?.value || s?.name || i
        const color = s?.streamLabel?.color || getThemeThenRandomSeriesColor({
          key,
          index: i,
          palette: THEME_COLOR_SWATCH,
          reservedColors: used
        })
        used.push(color)
        return color
      })
      // Compare streams: color by stream index
      return streamPalette[index] || getThemeThenRandomSeriesColor({ key: index, index, palette: THEME_COLOR_SWATCH })
    }

    // By category: use fixed workspace color order: 0, last, 1, last-1, 2, ...
    const key = entry?.metric || entry?.name || entry?.group || index
    const fill = getThemeThenRandomSeriesColor({ key, index, palette: THEME_COLOR_SWATCH })
    this.setState((prevState) => {
      const next = Array.isArray(prevState.groupColors) ? [...prevState.groupColors] : []
      next[index] = fill
      return { groupColors: next }
    })
    return fill
  }

  renderActiveShape = (props) => {
    const RADIAN = Math.PI / 180
    const { chartConfig } = this.state
    const {
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
      percent,
      value
    } = props

    const fixedDecimal = chartConfig.decimal || 0

    // Position the label inside the ring to avoid covering the legend or overflowing the chart area
    const SIN = Math.sin(-RADIAN * midAngle)
    const COS = Math.cos(-RADIAN * midAngle)
    // Safer radius toward the middle of the band to avoid clipping with inner/outer edges
    const rLabel = innerRadius + (outerRadius - innerRadius) * 0.5
    const xLabel = cx + rLabel * COS
    const yLabel = cy + rLabel * SIN
    const textAnchor = xLabel > cx ? 'start' : 'end'

    const textValue = `${chartConfig.prefix} ${value.toFixed(fixedDecimal)} ${chartConfig.suffix}`

    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        {/* thin highlight ring */}
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 4}
          outerRadius={outerRadius + 6}
          fill={fill}
        />
      </g>
    )
  }

  renderInactiveShape = (props) => {
    const {
      cx,
      cy,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
    } = props

    return (
      <Sector
        // style={{ filter: "drop-shadow( 3px 3px 2px rgba(0, 0, 0, .7))" }}
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
    )
  }

  renderPieLabel = (props) => {
    const { chartConfig } = this.state
    const { cx, cy, midAngle, innerRadius, outerRadius, percent, index, value } = props

    const fixedDecimal = chartConfig.decimal || 0
    const RADIAN = Math.PI / 180
    const SIN = Math.sin(-RADIAN * midAngle)
    const COS = Math.cos(-RADIAN * midAngle)

    const radius = innerRadius + (outerRadius - innerRadius) * 0.5
    const x = cx + radius * COS
    const y = cy + radius * SIN

    const textAnchor = x > cx ? 'start' : 'end'
    const textValue = `${chartConfig.prefix} ${formatYAxisLabel(value, chartConfig.format, chartConfig.decimal)} ${chartConfig.suffix}`

    return (
      <g>
        <text
          x={x - (COS >= 0 ? 1 : -1) * 12}
          y={y}
          dy={-16}
          dx={0}
          textAnchor={textAnchor}
          dominantBaseline="central"
          className='charts-no-cartesian__pie-label'
        >
          {textValue}
        </text>
        <text
          x={x}
          y={y}
          // dy={18} 
          textAnchor={textAnchor}
          dominantBaseline="central"
          className='charts-no-cartesian__pie-label'
        >
          {`${(percent * 100).toFixed(0)}%`}
        </text>
      </g>
    )
  }

  getUniqueGroupByValue = (uniqueGroupValue) => {
    let availableIndices = [...this.state.availableColorIndices]
    let uniqueValue = ''
    let uniqueOriginalValue = Object.entries(uniqueGroupValue)
      .filter(([key, value]) => key !== 'time')
      .map(([key, value]) => value)[0]
    const uniqueValueColors = { ...this.state.uniqueGroupByValue }

    if (availableIndices.length === 0) {
      availableIndices = Array.from(Array(GROUP_BY_UNIQUE_COLORS.length).keys())
    }

    if (uniqueOriginalValue && (typeof (uniqueOriginalValue) === 'string')) {
      uniqueValue = uniqueOriginalValue.replace(/\s+/g, '_').replace(/[^\w_]/g, '')
      this.setState({
        uniqueGroupByOriginalKey: {
          ...this.state.uniqueGroupByOriginalKey,
          [uniqueValue]: uniqueOriginalValue
        }
      })
    }

    if (!uniqueValueColors.hasOwnProperty(`${uniqueValue}`)) {
      const colorIndex = availableIndices.shift()
      uniqueValueColors[`${uniqueValue}`] = getThemeThenRandomSeriesColor({
        key: uniqueOriginalValue || uniqueValue,
        index: colorIndex,
        palette: THEME_COLOR_SWATCH
      })
      this.setState({
        uniqueGroupByValue: uniqueValueColors,
        availableColorIndices: availableIndices
      }, () => {
        if (this.props.onBarColorChange) {
          this.props.onBarColorChange(this.state.uniqueGroupByValue)
        }
      })
      return uniqueValueColors[uniqueValue]
    }
    return uniqueValueColors[uniqueValue] || ''
  }

  getDeviceList = () => {
    const { chartValues, devicesData } = this.props

    if (chartValues?.devices && !!chartValues.devices.length) {
      return chartValues.devices
    }

    return devicesData?.map(item => item.name)
  }

  setRawChartData = async () => {
    const { chart, dashboardTimeRange, isPreview, isNewVersion, chartValues, isDefaultTime } = this.props
    const { chartConfig } = this.state

    const actualTimeRange = getActualTimeRange(chart, dashboardTimeRange, isPreview, true, chartConfig)

    // Show loader immediately to avoid flashing error state
    this.setState({ isChartDataLoading: true })

    // Show loader immediately to avoid flashing error state
    this.setState({ isChartDataLoading: true })

    if (chartConfig.dataStreamSingle.breakdownType === 'compare_streams') {
      // Ensure every configured stream has at least one metric selected
      const streams = chartConfig?.dataStreamSingle?.dataStreams?.streams || chartConfig?.dataStreams?.streams || []
      const anyMissingMetrics = !streams.every(s => Array.isArray(s?.metric?.metrics) && s.metric.metrics.length > 0)
      if (anyMissingMetrics) {
        if (!this.props.isPreview) {
          // Suppress error on dashboard initial load
          return
        }
        return this.setState({
          chartData: {
            groupBy: [],
            data: []
          },
          dataErrorMessage: "Select a metric to display the preview",
          validData: false,
          isChartDataLoading: false
        })
      }
    }

    if (chartConfig.dataStreamSingle.breakdownType === 'by_category') {
      const hasMetrics = Array.isArray(chartConfig?.dataStreamSingle?.metric?.metrics) && chartConfig.dataStreamSingle.metric.metrics.length > 0
      if (!hasMetrics) {
        if (!this.props.isPreview) {
          // Suppress error on dashboard initial load
          return
        }
        return this.setState({
          chartData: {
            groupBy: [],
            data: []
          },
          dataErrorMessage: "Select a metric to display the preview",
          validData: false,
          isChartDataLoading: false
        })
      }
    }

    // if (chartValues?.devices?.length === 0) {
    //   return this.setState({
    //     chartData: {
    //       groupBy: [],
    //       data: []
    //     },
    //     dataErrorMessage: "Error: No devices selected",
    //     validData: false
    //   })
    // }

    // if (groupByMetrics.length === 0) {
    //   return this.setState({
    //     chartData: {
    //       groupBy: [],
    //       data: []
    //     },
    //     dataErrorMessage: "Select a group by to display the chart",
    //     validData: false,
    //     isChartDataLoading: false
    //   })
    // }

    if (this.props.sqlError) {
      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: this.props.sqlError,
        validData: false
      })
    }

    let data = {}

    const devices = this.getDeviceList();

    if (this.props.isPreview) {
      if (this.props.dataStreams[0]) {
        this.setState({ isChartDataLoading: true })

        if (!devices.length) {
          return this.setState({ isChartDataLoading: false })
        }

        const response = await previewRawChartData({
          dataStreams: this.props.dataStreams,
          visoDevices: devices,
          dashboardId: this.props.dashboardId
        })
        data = {
          errors: response.data.errors || null,
          data: response.data.data.previewRawChartData
        }

        // Background MAX fetch: transform query AVG( -> MAX( without schema changes
        try {
          const toMaxQuery = (q) => {
            if (!q || typeof q !== 'string') return q
            return q.replace(/AVG\s*\(/gi, 'MAX(')
          }
          const maxStreams = Array.isArray(this.props.dataStreams)
            ? this.props.dataStreams.map(s => ({ ...s, query: toMaxQuery(s?.query) }))
            : []
          if (maxStreams.length > 0) {
            const maxResp = await previewRawChartData({
              dataStreams: maxStreams,
              visoDevices: devices,
              dashboardId: this.props.dashboardId
            })
            if (!maxResp.data.errors && Array.isArray(maxResp.data.data?.previewRawChartData)) {
              const first = maxResp.data.data.previewRawChartData[0]
              const fields = first?.result?.fields || []
              const candidates = fields.filter(k => k && k !== 'bucketed_time')
              const maxKey = candidates[0]
              const values = (first?.result?.data || []).map(r => Number(r?.[maxKey]) || 0)
              const maxValue = values.length ? Math.max(...values) : null
              this.setState({ maxPreviewRawValue: maxValue })
            } else {
              this.setState({ maxPreviewRawValue: null })
            }
          }
        } catch (e) {
          this.setState({ maxPreviewRawValue: null })
        }

        this.setState({ isChartDataLoading: false })
      }
      this.setState({
        dataErrorMessage: "Select a metric to display the preview"
      })

    } else {
      this.setState({ isChartDataLoading: true })

      if (devices.length) {
        if (isDefaultTime) {

          const response = await rawChartData({
            chartId: this.props.chart?.id,
            visoDevices: this.props.deviceIds?.length ? this.props.deviceIds : devices,
            timeRange: { start: actualTimeRange.start, end: actualTimeRange.end }
          })
          this.setState({ isChartDataLoading: false })
          data = {
            errors: response.data.errors || null,
            data: response.data.data.rawChartData
          }
        } else {
          const response = await rawChartData({
            chartId: this.props.chart?.id,
            visoDevices: this.props.deviceIds?.length ? this.props.deviceIds : devices,
          })
          this.setState({ isChartDataLoading: false })
          data = {
            errors: response.data.errors || null,
            data: response.data.data.rawChartData
          }
        }
      }
    }

    if (data.errors) {
      const errorMessage = data.errors?.[0]?.message
      return this.setState({
        dataErrorMessage: errorMessage,
        validData: false,
        isChartDataLoading: false
      })
    }

    if (data.data) {

      const rawData = data.data.flatMap(item => item.result.data)
      const rawFields = data.data.flatMap(item => item.result.fields)
      const isSingleStream = Array.isArray(data.data) && data.data.length === 1
      if (isSingleStream && Array.isArray(rawData) && rawData.length > 0 && typeof this.state.maxPreviewRawValue === 'number' && this.state.maxPreviewRawValue > 0) {
        const fields0 = data.data[0]?.result?.fields || []
        const candidates = fields0.filter(k => k && k !== 'bucketed_time')
        const valueKey = candidates[0]
        const vals = (data.data[0]?.result?.data || []).map(r => Number(r?.[valueKey]) || 0)
        const val = vals.length ? (vals.reduce((s, v) => s + v, 0) / vals.length) : 0
        const percent = Math.max(0, Math.min(100, (val / this.state.maxPreviewRawValue) * 100))

        const colored = { name: 'value', metric: 'value', value: percent }
        const remainder = { name: 'remainder', metric: 'remainder', value: Math.max(0, 100 - percent), fill: '#eeeeee' }

        return this.setState({
          chartData: {
            groupBy: [],
            data: [colored, remainder],
            uniqueGroupValues: []
          },
          metricColumns: ['value'],
          dataErrorMessage: '',
          validData: true,
          isChartDataLoading: false,
          activeIndex: 0,
          actualDoughnutValue: val,
        })
      }

      let chartData = {};
      if (chartConfig.dataStreamSingle.breakdownType === 'compare_streams') {
        const streams = chartConfig?.dataStreamSingle?.dataStreams?.streams || chartConfig?.dataStreams?.streams || []
        chartData = {
          groupBy: rawFields,
          data: rawData.map((metric, index) => {
            const stream = streams[index] || {}
            const metricKey = (Array.isArray(stream?.metric?.metrics) && stream.metric.metrics.length > 0)
              ? stream.metric.metrics.join(', ')
              : Object.keys(metric)[0]

            return {
              name: metricKey,
              metric: metricKey,
              value: Number(metric[rawFields[index]]) || 0,
              group: rawFields[index],
              index: index
            };
          })
        };
      }

      if (chartConfig.dataStreamSingle.breakdownType === 'by_category') {
        const categoryField = rawFields[0];
        const metricField = rawFields[1];
        chartData = {
          groupBy: rawFields,
          data: rawData.map((metric, index) => {
            return {
              name: metric[categoryField], // name for legend/labels (e.g., "Claremont", "Rockingham")
              metric: metric[categoryField],
              value: Number(metric[metricField]) || 0,
              group: categoryField,
              index: index
            };
          })
        }
      };

      this.setState({
        chartData: {
          groupBy: chartData.groupBy,
          data: chartData.data,
          uniqueGroupValues: []
        },
        metricColumns: rawFields,
        dataErrorMessage: '',
        validData: true,
        isChartDataLoading: false,
        activeIndex: (this.state.activeIndex >= 0 ? this.state.activeIndex : (chartData.data?.length ? 0 : -1)),
        // cartesianConfig: mapChartValuesToCartesianConfig(this.state.chartConfig)
      })//, () => { console.log('After changes: ', this.state.cartesianConfig) })
    }
  }

  // Prevent unnecessary re-renders (e.g., from parent scroll)
  shouldComponentUpdate(nextProps, nextState) {
    return (
      !isEqual(this.props.height, nextProps.height) ||
      !isEqual(this.props.chartType, nextProps.chartType) ||
      !isEqual(this.props.chartValues, nextProps.chartValues) ||
      !isEqual(this.props.dataStreams, nextProps.dataStreams) ||
      !isEqual(this.props.previewParams, nextProps.previewParams) ||
      !isEqual(this.props.devicesData, nextProps.devicesData) ||
      !isEqual(this.props.chart, nextProps.chart) ||
      this.props.isEditMode !== nextProps.isEditMode ||
      !isEqual(this.state.chartData, nextState.chartData) ||
      !isEqual(this.state.chartConfig, nextState.chartConfig) ||
      this.state.isChartDataLoading !== nextState.isChartDataLoading ||
      this.state.activeIndex !== nextState.activeIndex
    );
  }

  getErrorMessage = () => {
    const {chartType, chartValues} = this.props;

    if (!this.state.validData) {
      return this.state.dataErrorMessage
    }

    if((chartType === 'pie' || chartType === 'gauge' || chartType === 'doughnut') && chartValues.dataStreamSingle?.breakdownType === 'by_category' && !chartValues.dataStreamSingle?.groupBy?.metric?.length){
      return "Select a group by to display the chart"
    }

    return ''
  }

  // Render error message
  renderErrorContext = () => {
    return (
      <div className='chart-error'>
        <div>
          {this.getErrorMessage()}
        </div>
        <div>
          {this.state.dataWarnMessage}
        </div>
      </div>
    )
  }

  hasError = () => {
    const {chartType, chartValues} = this.props;

    if (!this.state.validData) {
      return true
    }

    if((chartType === 'pie' || chartType === 'gauge' || chartType === 'doughnut') && chartValues.dataStreamSingle?.breakdownType === 'compare_streams'){
      return false;
    }

    if((chartType === 'pie' || chartType === 'gauge' || chartType === 'doughnut') && chartValues.dataStreamSingle?.breakdownType === 'by_category' && !chartValues.dataStreamSingle?.groupBy?.metrics?.length){
      return true;
    }

    return false
  }

  renderContent = () => {
    const { chart, height } = this.props
    const { chartData, metricColumns, validData, groupColors, chartConfig, dataWarnMessage, activeIndex } = this.state

    const formattedChartData = Array.isArray(chartData?.data) && chartData.data.map((d) => {
      return {
        ...d,
        bucketed_time: d.bucketed_time
      };
    });

    const baseRoundedData = roundData(formattedChartData, chartConfig.decimal);
    const processedChartData = (chartConfig.format === 'percent')
      ? normalizePercentPieData(baseRoundedData)
      : baseRoundedData;

    const isV2 = !!chartConfig && Object.prototype.hasOwnProperty.call(chartConfig, 'version')
    const titleText = (isV2 ? chartConfig?.chartTitle : (this.props.title || chartConfig?.chartTitle || this.props.chart?.title))
    // Determine breakdown type once; compute label per hovered slice inside tooltip
    const isCompareStreams = chartConfig?.dataStreamSingle?.breakdownType === 'compare_streams'
    const isByCategory = chartConfig?.dataStreamSingle?.breakdownType === 'by_category'

    // Follow-cursor tooltip only (no center text)
    const renderTooltipContent = ({ active, payload }) => {
      if (!active || !payload || !payload.length) return null
      const p = payload[0]
      const name = p?.payload?.metric || p?.name || p?.payload?.bucketed_time || ''
      if (name === 'remainder') return null
      const isValueSlice = name === 'value'
      const baseRaw = Number(p?.value) || 0
      const actualVal = (typeof this.state.actualDoughnutValue === 'number') ? this.state.actualDoughnutValue : null
      const maxVal = (typeof this.state.maxPreviewRawValue === 'number') ? this.state.maxPreviewRawValue : null
      const raw = isValueSlice && actualVal !== null ? actualVal : baseRaw

      const idx = p?.payload?.index !== undefined ? p.payload.index : (Array.isArray(processedChartData)
        ? processedChartData.findIndex(d => d === p?.payload)
        : -1)
      const streamLabelLocal = (idx >= 0)
        ? (
          isCompareStreams
            ? chartConfig?.dataStreamSingle?.dataStreams?.streams?.[idx]?.streamLabel?.value
            : chartConfig?.dataStreams?.streams?.[idx]?.streamLabel?.value
        )
        : ''

      // In by_category mode, legends use the category value itself as the label.
      // Mirror that here so hover labels exactly match legend labels.
      const displayLabel = isByCategory ? name : (streamLabelLocal || name)

      const total = Array.isArray(processedChartData)
        ? processedChartData.reduce((s, d) => s + (Number(d?.value) || 0), 0)
        : 0
      const percentNum = (isValueSlice && maxVal && maxVal > 0)
        ? ((actualVal || 0) / maxVal) * 100
        : (total ? ((Number(baseRaw) || 0) / total) * 100 : 0)
      const swatchColor = (idx >= 0) ? this.getStrokeLine(processedChartData[idx], idx) : '#888'
      const formatted = `${chartConfig.prefix} ${formatYAxisLabel(raw, chartConfig.format, chartConfig.decimal)} ${chartConfig.suffix}`
      return (
        <div style={{ background: '#fff', border: '1px solid #e6e6e6', borderRadius: 8, padding: '6px 10px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          {name && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#666', marginBottom: 2 }}>
              <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: swatchColor }} />
              <span>{displayLabel}</span>
            </div>
          )}
          <div style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>{formatted}</div>
          {<div style={{ fontSize: 11, color: '#777', marginTop: 2 }}>{percentNum.toFixed(0)}%</div>}
        </div>
      )
    }

    const activeIdx = (typeof activeIndex === 'number' && activeIndex >= 0) ? activeIndex : (processedChartData?.length ? 0 : -1)

    return (
      <React.Fragment>
        {
          this.hasError()
            ?
            this.renderErrorContext()
            :
            <div className='height-100'>
              <div className='grid-row height-100'>
                <div className='width-100'>
                  <div className='grid-col'>
                    <div className='job-training__title job-training__title2'>{titleText}</div>
                  </div>
                </div>
                <div className='job-training__charts2 job-training__chart_container'>
                  <ResponsiveContainer width="100%" height={height}>
                    <PieChart margin={{ top: 0, right: 20, left: 20, bottom: 24 }} height={height}>
                      {processedChartData.map((entry, index) => {
                        if (entry?.metric === 'remainder' || entry?.name === 'remainder') {
                          return null
                        }
                        const fillId = `${this.props.chart.id}_${(entry?.metric || entry?.name || entry?.group)?.replaceAll(' ', '')}_${index}`
                        const stroke = this.getStrokeLine(entry, index)
                        return (
                          <React.Fragment key={`fill-${index}`}>
                            {chartGradientDefs(fillId, stroke)}
                          </React.Fragment>
                        )
                      })}
                      <Pie
                        data={processedChartData}
                        activeIndex={activeIdx}
                        activeShape={this.renderActiveShape}
                        inactiveShape={this.renderInactiveShape}
                        labelLine={false}
                        label={chartConfig?.enablePieChartLabels ? this.renderPieLabel : null}
                        dataKey="value"
                        nameKey="metric"
                        cx="50%"
                        cy="55%"
                        outerRadius='100%'
                        innerRadius='75%'
                        rootTabIndex={33}
                        className=''
                        paddingAngle={1}
                        isAnimationActive={false}
                        onMouseEnter={undefined}
                        onMouseLeave={undefined}
                      >
                        {processedChartData.map((entry, index) => {
                          const isRemainder = (entry?.metric === 'remainder' || entry?.name === 'remainder')
                          const fillId = `${this.props.chart.id}_${(entry?.metric || entry?.name || entry?.group)?.replaceAll(' ', '')}_${index}`
                          const fill = isRemainder ? '#eeeeee' : `url(#${fillId}Fill)`

                          return (
                            <Cell
                              key={`cell-${index}`}
                              fill={fill}
                              onMouseEnter={isRemainder ? undefined : (_, i) => this.onDoughnutEnter(_, index)}
                              onMouseLeave={isRemainder ? undefined : this.onDoughnutLeave}
                              style={isRemainder ? { cursor: 'default', pointerEvents: 'none' } : {}}
                            />
                          )
                        })}

                        {/* Centered data title (restored) */}
                        {chartConfig.enableDataTitle && chartConfig.dataTitle && (
                          <Label
                            position="center"
                            value={chartConfig.dataTitle}
                            className='charts-no-cartesian__doughnut-label'
                          />
                        )}

                      </Pie>

                      {/* Hover tooltip */}
                      <Tooltip
                        isAnimationActive={false}
                        wrapperStyle={{ pointerEvents: 'none', zIndex: 6 }}
                        cursor={{ fill: 'transparent' }}
                        content={renderTooltipContent}
                      />

                      {chartConfig.legend !== 'none' && (() => {
                        // Start from shared legendPayload (which already filters synthetic
                        // slices like remainder), but override colors using the same
                        // getStrokeLine logic that is used for slices so legend colors
                        // always match the chart.
                        const basePayload = legendPayload(processedChartData, chartConfig.dataOverrides, chartConfig) || []
                        const coloredPayload = basePayload.map((entry, idx) => {
                          const original = entry?.payload || entry
                          const color = this.getStrokeLine(original, idx)
                          return { ...entry, color }
                        })

                        return (
                          <Legend
                            payload={coloredPayload}
                            verticalAlign={chartConfig.legend === 'right' ? 'middle' : 'bottom'}
                            align={chartConfig.legend === 'right' ? 'right' : 'center'}
                            layout={chartConfig.legend === 'right' ? 'vertical' : 'horizontal'}
                            iconType='square'
                            iconSize={14}
                            height={20}
                            chartHeight={height}
                            content={(props) => legendContent({ ...props, payload: coloredPayload, dataOverrides: chartConfig.dataOverrides, chartConfig })}
                            wrapperStyle={{
                              top: chartConfig.legend === 'right' && '50%',
                              transform: chartConfig.legend === 'right' && 'translateY(-50%)',
                              height: chartConfig.legend === 'right' && 'auto',
                              bottom: chartConfig.legend === 'right' && 'auto'
                            }}
                          />
                        )
                      })()}
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
              {dataWarnMessage && <div className='chart-error'>
                {dataWarnMessage}
              </div>
              }
            </div>
        }
      </React.Fragment>
    )
  }

  render() {
    const { chart, isLegacy, color, isEditMode } = this.props;

    return (
      <Card
        noGrid
        color={this.props.color}
        menu={this.state.hasMenu && !isLegacy ? 'dot' : ''}
        chartId={!this.state.isChartDataLoading && (chart?.id || '66dece43216604577e3ab1a0')}
        contentLoading={this.state.isChartDataLoading}
        className={classNames("overview chart-cards", {
          "charts-card-preview": this.props.isPreview
        })}
        height={this.props.isPreview && this.props.height + 85}
        content={this.state.isChartDataLoading ? <ChartLoader color={this.props.color} /> : this.renderContent()}
        onToogleMenu={this.props.onToogleMenu}
        dropDownMenu={!this.props.isPreview && this.props.onEdit && chartDotMenu({
          color,
          showAI: this.props.showAI,
          onEdit: this.props.onEdit,
          onCopy: this.props.onCopy,
          onDelete: this.props.onDelete,
          onExplainKPI: () => this.props.onExplainKPI(chart?.id),
          onImproveChart: () => this.props.onImproveChart(chart?.id),
          isEditMode
        })}
      >
      </Card>
    )
  }
}

DoughnutChart.propTypes = {
  onEdit: PropTypes.func,
  model: PropTypes.object,
  chart: PropTypes.any,
  previewParams: PropTypes.any,
  dashboardDevices: PropTypes.any,
  dashboardTimeRange: PropTypes.any,
  height: PropTypes.any,
  isPreview: PropTypes.bool,
  refreshPreview: PropTypes.bool,
  groupByColors: PropTypes.object
};

export default DoughnutChart;